<script setup>
import { ref, reactive, onMounted } from 'vue';
import AccountSettingsLayout from "@/pages/UserProfile/AccountSettingsLayout.vue";
import SubscriptionsCard from "@/pages/UserProfile/integrations/SubscriptionsCard.vue";
import AccountSettingsCard from "@/pages/UserProfile/integrations/AccountSettingsCard.vue";
import PaymentHistoryCard from "@/pages/UserProfile/integrations/PaymentHistoryCard.vue";
import IntegrationsCard from "@/pages/UserProfile/integrations/IntegrationsCard.vue";
import ChangeEmailModal from "@/pages/UserProfile/modals/ChangeEmailModal.vue";
import ChangePasswordModal from "@/pages/UserProfile/modals/ChangePasswordModal.vue";
import IntegrationDetailsModal from "@/pages/UserProfile/modals/IntegrationDetailsModal.vue";
import PaymentHistoryModal from "@/pages/UserProfile/modals/PaymentHistoryModal.vue";
import NavigationBar from "@/components/NavigationBar.vue";
import { Api } from "@/service/api";
import { mapIntegrationsReport } from "@/pages/UserProfile/utils/integrationMapper";
import { mapPaymentsHistory } from "@/pages/UserProfile/utils/paymentHistoryMapper";
import { mapUserInfo } from "@/pages/UserProfile/utils/userMapper";
import { mapSubscription } from "@/pages/UserProfile/utils/subscriptionMapper";

const subscription = reactive({
    plan: { name: '—', status: '—', expires: '—', isBasePlan: false },
    stats: {
        transactions: { current: 0, total: 0 },
        flows: { current: 0, limit: 'Без ограничений' }
    },
    payment: { card: '—' }
});

const user = reactive({
    email: '—',
    passwordUpdatedAt: '—'
});

const paymentHistory = ref([]);

const integrationsList = ref([]);

const isEmailChangeWindowVisible = ref(false);
const isPasswordChangeWindowVisible = ref(false);
const isIntegrationDetailsVisible = ref(false);
const isPaymentHistoryVisible = ref(false);

onMounted(() => {
    loadUser();
    loadIntegrations();
    loadPaymentHistory();
});

async function loadUser() {
    try {
        const userInfo = await Api.getUserMeInfo();
        Object.assign(user, mapUserInfo(userInfo));
        Object.assign(subscription, mapSubscription(userInfo));
    } catch (error) {
        console.error('Failed to load user info', error);
    }
}

async function loadIntegrations() {
    try {
        const report = await Api.getUserIntegrationsReport();
        integrationsList.value = mapIntegrationsReport(report);
    } catch (error) {
        console.error('Failed to load integrations report', error);
    }
}

async function loadPaymentHistory() {
    try {
        const history = await Api.getUserPaymentsHistory();
        paymentHistory.value = mapPaymentsHistory(history);
    } catch (error) {
        console.error('Failed to load payment history', error);
    }
}

function openEmailChangeWindow() {
    isEmailChangeWindowVisible.value = true;
}

function openPasswordChangeWindow() {
    isPasswordChangeWindowVisible.value = true;
}

function openIntegrationDetailsWindow() {
    isIntegrationDetailsVisible.value = true;
}

function openPaymentHistoryWindow() {
    isPaymentHistoryVisible.value = true;
}

</script>

<template>
    <div class="flex">
        <NavigationBar/>
        <AccountSettingsLayout title="Настройки аккаунта" subtitle="Управление подпиской и данными">
            <!-- Верхний блок -->
            <SubscriptionsCard
                :plan="subscription.plan"
                :stats="subscription.stats"
                :payment="subscription.payment"
                class="mb-6"
            />

            <!-- Сетка 2 колонки -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">

                <!-- Левая колонка -->
                <div class="flex flex-col gap-6">
                    <AccountSettingsCard
                        :user="user"
                        @open-change-email-modal="openEmailChangeWindow"
                        @open-change-password-modal="openPasswordChangeWindow"
                    />
                </div>

                <!-- Правая колонка -->
                <div class="flex flex-col gap-6">
                    <PaymentHistoryCard
                        :transactions="paymentHistory.slice(0, 3)"
                        @open-payment-history="openPaymentHistoryWindow"
                    />
                    <IntegrationsCard
                        :integrations="integrationsList"
                        @open-integrations-details="openIntegrationDetailsWindow"
                    />
                </div>

            </div>
        </AccountSettingsLayout>
    </div>
    <ChangeEmailModal v-model="isEmailChangeWindowVisible" />
    <ChangePasswordModal
        v-model="isPasswordChangeWindowVisible"
        @password-changed="loadUser"
    />
    <IntegrationDetailsModal
        v-model="isIntegrationDetailsVisible"
        :integrations="integrationsList"
        @refresh-integrations="loadIntegrations"
    />
    <PaymentHistoryModal
        v-model="isPaymentHistoryVisible"
        :transactions="paymentHistory"
    />

</template>